import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Inbox, RefreshCw, CheckCircle, XCircle, AlertCircle, RotateCcw,
  MoreHorizontal, Eye, Clock, Send } from
'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  received: 'bg-blue-500/20 text-blue-400',
  validated: 'bg-cyan-500/20 text-cyan-400',
  posted: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400'
};

const eventTypeLabels = {
  'PAYROLL_POSTED': { label: 'Payroll Posted', color: 'bg-purple-500/20 text-purple-400' },
  'AR_INVOICE_POSTED': { label: 'AR Invoice', color: 'bg-green-500/20 text-green-400' },
  'AP_BILL_POSTED': { label: 'AP Bill', color: 'bg-amber-500/20 text-amber-400' },
  'AR_RECEIPT_POSTED': { label: 'AR Receipt', color: 'bg-blue-500/20 text-blue-400' },
  'AP_PAYMENT_POSTED': { label: 'AP Payment', color: 'bg-red-500/20 text-red-400' }
};

export const EventInbox = () => {const { t } = useTranslation("finance");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetails, setShowDetails] = useState(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/accounting/events?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch events');
      const data = await res.json();
      setEvents(data.events || []);
    } catch (err) {
      toast.error(err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchEvents();}, [statusFilter]);

  const handleReplay = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/events/${id}/replay`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to replay event');
      toast.success('Event replayed successfully');
      fetchEvents();
    } catch (err) {toast.error(err.message);}
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/events/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch event details');
      const data = await res.json();
      setShowDetails(data);
    } catch (err) {toast.error(err.message);}
  };

  const receivedCount = events.filter((e) => e.status === 'received').length;
  const postedCount = events.filter((e) => e.status === 'posted').length;
  const rejectedCount = events.filter((e) => e.status === 'rejected').length;

  return (
    <div className="space-y-6" data-testid="event-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("eventInbox", "Event Inbox")}</h1>
          <p className="text-muted-foreground mt-1">{t("processAccountingEvents", "Process accounting events from HR, Finance, and other modules")}</p>
        </div>
        <Button variant="outline" onClick={fetchEvents} className="gap-2">
          <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalEvents", "Total Events")}</p>
                <p className="text-3xl font-bold">{events.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><Inbox className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-3xl font-bold">{receivedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20"><Clock className="h-6 w-6 text-amber-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("posted", "Posted")}</p>
                <p className="text-3xl font-bold">{postedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><CheckCircle className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("rejected", "Rejected")}</p>
                <p className="text-3xl font-bold">{rejectedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20"><XCircle className="h-6 w-6 text-red-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("events", "Events")}</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="received">{t("received", "Received")}</SelectItem>
                <SelectItem value="validated">{t("validated", "Validated")}</SelectItem>
                <SelectItem value="posted">{t("posted", "Posted")}</SelectItem>
                <SelectItem value="rejected">{t("rejected", "Rejected")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          events.length === 0 ?
          <div className="text-center py-12">
              <Inbox className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noEventsIn", "No events in inbox")}</p>
              <p className="text-sm text-muted-foreground mt-2">{t("eventsWillAppear", "Events will appear here when posted from HR or Finance modules")}</p>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("eventId", "Event ID")}</th>
                    <th className="text-left p-3 font-medium">{t("type", "Type")}</th>
                    <th className="text-left p-3 font-medium">{t("source", "Source")}</th>
                    <th className="text-left p-3 font-medium">{t("received", "Received")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {events.map((event) => {
                  const typeInfo = eventTypeLabels[event.event_type] || { label: event.event_type, color: 'bg-gray-500/20 text-gray-400' };
                  return (
                    <tr key={event.id} className="hover:bg-slate-800/30">
                        <td className="p-3 font-mono text-sm">{event.event_id?.slice(0, 8) || `#${event.id}`}</td>
                        <td className="p-3">
                          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{event.source_module}</span>
                          {event.source_doc_type &&
                        <span className="text-muted-foreground text-sm"> / {event.source_doc_type}</span>
                        }
                        </td>
                        <td className="p-3 text-sm">{new Date(event.received_at).toLocaleString()}</td>
                        <td className="p-3">
                          <Badge className={statusColors[event.status]}>{event.status}</Badge>
                        </td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewDetails(event.id)}>
                                <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                            </DropdownMenuItem>
                              {(event.status === 'rejected' || event.status === 'received') &&
                            <DropdownMenuItem onClick={() => handleReplay(event.id)}>
                                  <RotateCcw className="h-4 w-4 mr-2" />{t("replay", "Replay")}
                            </DropdownMenuItem>
                            }
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("eventDetails", "Event Details")}</DialogTitle>
          </DialogHeader>
          {showDetails &&
          <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("eventId", "Event ID")}</p>
                  <p className="font-mono text-sm">{showDetails.event_id}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("eventType", "Event Type")}</p>
                  <p>{showDetails.event_type}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("source", "Source")}</p>
                  <p>{showDetails.source_module} / {showDetails.source_doc_type}</p>
                </div>
              </div>
              {showDetails.rejection_reason &&
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm text-muted-foreground">{t("rejectionReason", "Rejection Reason")}</p>
                  <p className="text-red-400">{showDetails.rejection_reason}</p>
                </div>
            }
              {showDetails.journal_entry_id &&
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-muted-foreground">{t("createdJournalEntry", "Created Journal Entry")}</p>
                  <p className="text-green-400">{t("journal", "Journal #")}{showDetails.journal_entry_id}</p>
                </div>
            }
              {showDetails.payload_json &&
            <div>
                  <p className="text-sm text-muted-foreground mb-2">{t("eventPayload", "Event Payload")}</p>
                  <pre className="p-3 rounded-lg bg-slate-800/50 overflow-x-auto text-xs">
                    {JSON.stringify(showDetails.payload_json, null, 2)}
                  </pre>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
            {showDetails && (showDetails.status === 'rejected' || showDetails.status === 'received') &&
            <Button onClick={() => {handleReplay(showDetails.id);setShowDetails(null);}} className="bg-green-600 hover:bg-green-700">
                <RotateCcw className="h-4 w-4 mr-2" />{t("replayEvent", "Replay Event")}
            </Button>
            }
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default EventInbox;